import { AccountFilters } from "@services/account/types";
import { AuditFilters } from "@services/audit/types";
import { CatalogFilters } from "@services/catalog/types";
import { DeviceFilters } from "@services/devices/types";
import { MeetingInstanceFilters } from "@services/meetings/types";
import { RoomsFilters } from "@services/rooms/types";
import { TicketsFilters } from "@services/tickets/types";
import { UserFilters } from "@services/user/types";
import { WorkflowsFilters } from "@services/workflows/types";
import { createSearchParams } from "react-router-dom";
import mappers from ".";

type AnyFilters =
  | RoomsFilters
  | DeviceFilters
  | MeetingInstanceFilters
  | TicketsFilters
  | AccountFilters
  | UserFilters
  | WorkflowsFilters
  | AuditFilters
  | CatalogFilters;
export const SLFilterToSearch = (filters: AnyFilters) => {
  return objectToSearchParams(filters);
};

export const objectToSearchParams = (filters: object) => {
  const params: Record<string, string | string[] | undefined> = {};
  (Object.keys(filters) as (keyof typeof filters)[]).map((key) => {
    if (filters[key] !== undefined && filters[key] !== null)
      params[key] =
        typeof filters[key] === typeof [""]
          ? (filters[key] as string[])
          : `${filters[key]}`;
  });
  return createSearchParams(params as Record<string, string | string[]>);
};

const otherSearchKey = ["source", "service", "status", "producerIds"];
const excludePayloadFilters = ["target", "tags", "room.details.roomType"];
const timeFilters = [
  "timeLeaseExpiration",
  "timeWarrantyExpiration",
  "time",
  "assignee",
  "timeServiceExpiration",
];
const arrayToStringFilters = ["accountId", "category"];

export const searchParamsToSearchObject = (
  searchParams: URLSearchParams,
  type: UserWidgetType | UserOtherType | undefined
): Record<string, string | string[] | Record<string, unknown>> => {
  if (!type || searchParams.get("mode") === "filter") return {};

  const searchFilters = mappers[type].fromSearchParams.toSL(searchParams);
  const specialFilters =
    mappers[type].fromSearchParams.toDefinition(searchParams);

  const result: Record<string, string | string[] | Record<string, unknown>> =
    {};

  const stringify = (val: unknown): string =>
    typeof val === "string" ||
    typeof val === "number" ||
    typeof val === "boolean"
      ? String(val)
      : JSON.stringify(val);

  Object.entries(searchFilters).forEach(([key, value]) => {
    if (key === "mode" || value === undefined) return;

    if (Array.isArray(value)) {
      const cleanedValues = value.filter((v) => v !== undefined).map(stringify);
      if (cleanedValues.length > 0) {
        result[key] = arrayToStringFilters.includes(key)
          ? cleanedValues.join(",")
          : cleanedValues;
      }
    } else {
      result[key] = stringify(value);
    }
  });

  Object.values(specialFilters).forEach(
    ({ filterType, filterValue, payload }) => {
      if (!filterType) return;

      const isOtherKey = otherSearchKey.includes(filterType);

      if (isOtherKey && payload && !filterValue) {
        result[filterType === "producerIds" ? "additionalSearch" : filterType] =
          payload as Record<string, unknown>;
        return;
      }

      if (filterValue && timeFilters.includes(filterType)) {
        result[filterType] = filterValue;
      }

      if (payload && !excludePayloadFilters.includes(filterType)) {
        result[`${filterType}-customDate`] = payload as Record<string, unknown>;
      }
    }
  );

  return result;
};

export const asBoolean = <T extends AnyFilters>(
  filters: T,
  params: URLSearchParams,
  keys: Array<keyof T>
) =>
  keys.forEach((key) => {
    //@ts-ignore
    if (params.get(key) !== null) filters[key] = params.get(key) === "true";
  });

export const asString = <T extends AnyFilters>(
  filters: T,
  params: URLSearchParams,
  keys: Array<keyof T>
) =>
  keys.forEach((key) => {
    //@ts-ignore
    filters[key] = params.get(key) ?? undefined;
  });

export const asNumber = <T extends AnyFilters>(
  filters: T,
  params: URLSearchParams,
  keys: Array<keyof T>
) =>
  keys.forEach((key) => {
    //@ts-ignore
    if (params.get(key) !== null) filters[key] = parseInt(params.get(key));
  });

export const asNonEmptyArray = <T extends AnyFilters>(
  filters: T,
  params: URLSearchParams,
  keys: Array<keyof T>
) =>
  keys.forEach((key) => {
    //@ts-ignore
    if (params.getAll(key).length > 0) filters[key] = params.getAll(key);
  });
